import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Calendar as CalendarIcon,
  Clock,
  ChevronRight,
  ChevronLeft,
  User as UserIcon,
  CheckCircle,
  AlertCircle,
  Navigation,
  Loader2,
  ShieldCheck,
  ExternalLink
} from 'lucide-react';
import {
  Venue,
  Service,
  AddOn,
  BookingState,
  Appointment,
  Company,
  CheckoutPricing,
  BookingAvailabilitySlot
} from '@/types';
import { COUNTRY_OPTIONS, getDefaultCountryCode } from '@/constants';
import { api } from '@/services/api';
import { tokenizeCardDetails } from '@/services/opayo';
import {
  filterPastAvailabilitySlots,
  getAvailableTimeSlotsForDate,
  isDateBlockedBySchedule
} from '@/lib/scheduling';
import {
  formatCardNumber,
  formatDobInput,
  formatExpiry,
  normalizeDobToIso,
  isValidDob,
  isValidEmail
} from '@/lib/formatters';
import { buildMapById } from '@/lib/collections';
import { isValidUkPhone, normalizeUkPhone } from '@/lib/phone';
import { buildBrowserInfo } from '@/lib/opayo';
import { getTierPrice } from '@/lib/pricing';
import { getGoogleMapsUrl, getVenueMapUrl } from '@/lib/venueMaps';
import { shouldShowInlineFormError } from '@/lib/bookingFlowErrors';
import { normalizeErrorMessage } from '@/lib/errorUtils';

interface BookingFlowProps {
  venues: Venue[];
  services: Service[];
  addOns: AddOn[];
  servicesById?: Map<string, Service>;
  venuesById?: Map<string, Venue>;
  addOnsById?: Map<string, AddOn>;
  appointments: Appointment[];
  onComplete: (booking: Appointment) => Promise<void> | void;
  authenticatedCompany?: Company;
  termsAndConditions: string;
  onBack?: () => void;
}

type ThreeDSData = {
  acsUrl: string;
  cReq: string;
  termUrl: string;
  threeDSSessionData?: string;
};

type PaymentResultModal = {
  type: 'success' | 'error';
  title?: string;
  message: string;
  shouldFinalize: boolean;
};

const RESERVED_PORTAL_EMAIL_ERROR =
  'This email is a portal login email. Enter the actual patient email for this booking.';

function getDistanceFromLatLonInMiles(lat1: number, lon1: number, lat2: number, lon2: number) {
  var R = 3958.8;
  var dLat = deg2rad(lat2 - lat1);
  var dLon = deg2rad(lon2 - lon1);
  var a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function deg2rad(deg: number) {
  return deg * (Math.PI / 180);
}

export const BookingFlow: React.FC<BookingFlowProps> = ({
  venues,
  services,
  addOns,
  servicesById: servicesByIdFromProps,
  venuesById: venuesByIdFromProps,
  addOnsById: addOnsByIdFromProps,
  appointments,
  onComplete,
  authenticatedCompany,
  termsAndConditions,
  onBack
}) => {
  const [threeDSData, setThreeDSData] = useState<ThreeDSData | null>(null);
  const [state, setState] = useState<BookingState>({
    step: 0,
    mode: authenticatedCompany ? 'company' : 'individual',
    venueId: null,
    serviceId: null,
    addOnIds: [],
    date: null,
    time: null,
    customerDetails: { name: '', email: '', phone: '', dob: '', jobTitle: '', poNumber: '' }
  });

  const currentCompany = authenticatedCompany || null;
  const servicesById = useMemo(
    () => servicesByIdFromProps ?? buildMapById(services),
    [servicesByIdFromProps, services]
  );
  const addOnsById = useMemo(
    () => addOnsByIdFromProps ?? buildMapById(addOns),
    [addOnsByIdFromProps, addOns]
  );
  const venuesById = useMemo(
    () => venuesByIdFromProps ?? buildMapById(venues),
    [venuesByIdFromProps, venues]
  );
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const [cardDetails, setCardDetails] = useState({
    number: '',
    expiry: '',
    cvc: '',
    cardholderName: ''
  });
  const [billingAddress, setBillingAddress] = useState({
    address1: '',
    city: '',
    postalCode: '',
    country: getDefaultCountryCode()
  });
  const [userPostcode, setUserPostcode] = useState('');
  const [distances, setDistances] = useState<Record<string, number>>({});
  const [isSearchingPostcode, setIsSearchingPostcode] = useState(false);
  const [postcodeError, setPostcodeError] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [formError, setFormError] = useState('');
  const [dobTouched, setDobTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [paymentResultModal, setPaymentResultModal] = useState<PaymentResultModal | null>(null);
  const [checkoutPricing, setCheckoutPricing] = useState<CheckoutPricing | null>(null);
  const [pendingPaymentId, setPendingPaymentId] = useState<string | undefined>(undefined);
  const [pendingBookingRef, setPendingBookingRef] = useState<string | null>(null);
  const [availabilitySlots, setAvailabilitySlots] = useState<BookingAvailabilitySlot[]>([]);
  const [hasAvailabilityData, setHasAvailabilityData] = useState(false);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [isFindingNextAvailable, setIsFindingNextAvailable] = useState(false);
  const [nextAvailableMessage, setNextAvailableMessage] = useState<string | null>(null);
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const dobValue = state.customerDetails.dob.trim();
  const isDobValid = isValidDob(dobValue);
  const showDobError = dobTouched && !isDobValid;

  const emailValue = state.customerDetails.email.trim();
  const isEmailValid = isValidEmail(emailValue);
  const showEmailError = emailTouched && !isEmailValid;

  const phoneValue = state.customerDetails.phone.trim();
  const isPhoneValid = !phoneValue || isValidUkPhone(phoneValue);
  const showPhoneError = phoneTouched && !isPhoneValid;

  const getMinBookableDate = () => {
    const minDate = new Date();
    minDate.setHours(0, 0, 0, 0);
    return minDate;
  };

  const getPricingForCheckout = useCallback((): CheckoutPricing => {
    if (checkoutPricing) return checkoutPricing;
    let subtotalAmount = 0;
    const service = state.serviceId ? servicesById.get(state.serviceId) : undefined;
    if (service) subtotalAmount += getPrice(service);
    state.addOnIds.forEach((id) => {
      const addon = addOnsById.get(id);
      if (addon) subtotalAmount += getPrice(addon);
    });
    return {
      currency: 'GBP',
      subtotalAmount,
      vatRatePercent: 0,
      vatAmount: 0,
      totalAmount: subtotalAmount
    };
  }, [checkoutPricing, state.serviceId, state.addOnIds, servicesById, addOnsById, currentCompany]);

  const findNextAvailableSlot = useCallback(async () => {
    if (isFindingNextAvailable) return;
    if (!state.venueId || !state.serviceId) {
      setNextAvailableMessage('Select a location and service to find availability.');
      return;
    }

    setIsFindingNextAvailable(true);
    setNextAvailableMessage(null);

    try {
      const maxDays = 60;
      const minDate = getMinBookableDate();
      const startDate = state.date ? new Date(state.date) : minDate;
      startDate.setHours(0, 0, 0, 0);
      if (startDate < minDate) {
        startDate.setTime(minDate.getTime());
      }

      const startOffset = state.date ? 1 : 0;
      for (let i = startOffset; i < maxDays; i += 1) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        const result = await api.getBookingAvailability({
          venueId: state.venueId,
          date,
          serviceId: state.serviceId,
          addOnIds: state.addOnIds
        });
        const slots = filterPastAvailabilitySlots(date, result?.slots || []);
        const availableSlot = slots.find((slot) => slot.available);
        if (availableSlot) {
          setState((prev) => ({ ...prev, date, time: availableSlot.time }));
          setVisibleMonth(new Date(date.getFullYear(), date.getMonth(), 1));
          setNextAvailableMessage(
            `Next available: ${date.toLocaleDateString('en-GB', {
              dateStyle: 'full'
            })} at ${availableSlot.time}.`
          );
          return;
        }
      }

      setNextAvailableMessage(
        `No availability in the next ${maxDays} days. Try another location or service.`
      );
    } catch (_err) {
      setNextAvailableMessage('Unable to load next available appointments right now.');
    } finally {
      setIsFindingNextAvailable(false);
    }
  }, [state.venueId, state.serviceId, state.addOnIds, state.date, isFindingNextAvailable]);

  const finalizeBooking = useCallback(async () => {
    const bookingRef = pendingBookingRef || `K2Ref-${Math.floor(1000 + Math.random() * 9000)}`;
    const selectedService = state.serviceId ? servicesById.get(state.serviceId) : undefined;
    const pricing = getPricingForCheckout();
    const phoneValue = state.customerDetails.phone.trim();
    const hasQuestionnaire =
      Boolean(selectedService?.questionnaireConfig?.templateId) ||
      state.addOnIds.some((id) => addOnsById.get(id)?.questionnaireConfig?.templateId);

    const newAppointment: Appointment = {
      id: bookingRef,
      venueId: state.venueId!,
      serviceId: state.serviceId!,
      addOnIds: state.addOnIds,
      date: state.date!.toISOString(),
      time: state.time!,
      customerName: state.customerDetails.name,
      customerEmail: state.customerDetails.email,
      customerPhone: phoneValue ? normalizeUkPhone(phoneValue) : '',
      customerDob: normalizeDobToIso(state.customerDetails.dob),
      customerJobTitle: state.customerDetails.jobTitle,
      companyId: currentCompany?.id,
      poNumber:
        state.mode === 'company' || authenticatedCompany
          ? state.customerDetails.poNumber
          : undefined,
      paymentMethod:
        state.mode === 'company' || authenticatedCompany
          ? currentCompany?.paymentPreference || 'invoice'
          : 'card',
      paymentId: pendingPaymentId,
      subtotalAmount: pricing.subtotalAmount,
      vatRatePercent: pricing.vatRatePercent,
      vatAmount: pricing.vatAmount,
      totalAmount: pricing.totalAmount,
      currency: pricing.currency,
      status: 'booked',
      createdAt: new Date().toISOString(),
      questionnaireStatus: hasQuestionnaire ? 'pending' : 'not_required'
    };
    try {
      await Promise.resolve(onComplete(newAppointment));
      setIsProcessingPayment(false);
      setPendingPaymentId(undefined);
      setPendingBookingRef(null);
    } catch (err: unknown) {
      const message = normalizeErrorMessage(err, 'Failed to complete booking.');
      setFormError(message);
      setPaymentResultModal({
        type: 'error',
        title: 'Booking Failed',
        message,
        shouldFinalize: false
      });
      setIsProcessingPayment(false);
    }
  }, [
    servicesById,
    addOnsById,
    state,
    currentCompany,
    authenticatedCompany,
    onComplete,
    pendingPaymentId,
    pendingBookingRef,
    getPricingForCheckout
  ]);

  useEffect(() => {
    if (authenticatedCompany) {
      setState((prev) => ({ ...prev, mode: 'company' }));
    }
  }, [authenticatedCompany]);

  useEffect(() => {
    setNextAvailableMessage(null);
  }, [state.venueId, state.serviceId, state.addOnIds]);

  useEffect(() => {
    if (!currentCompany?.allowedVenueIds?.length || !state.venueId) return;
    if (currentCompany.allowedVenueIds.includes(state.venueId)) return;
    setState((prev) => ({ ...prev, venueId: null, date: null, time: null }));
  }, [currentCompany, state.venueId]);

  useEffect(() => {
    if (!state.serviceId) {
      setCheckoutPricing(null);
      return;
    }

    let isCurrent = true;
    api
      .getCheckoutQuote({
        serviceId: state.serviceId,
        addOnIds: state.addOnIds,
        companyId: currentCompany?.id,
        currency: 'GBP'
      })
      .then((result) => {
        if (isCurrent) setCheckoutPricing(result.pricing);
      })
      .catch(() => {
        if (isCurrent) setCheckoutPricing(null);
      });

    return () => {
      isCurrent = false;
    };
  }, [state.serviceId, state.addOnIds, currentCompany?.id]);

  useEffect(() => {
    if (!state.date || !state.venueId || !state.serviceId) {
      setAvailabilitySlots([]);
      setHasAvailabilityData(false);
      setIsLoadingAvailability(false);
      return;
    }

    let isCurrent = true;
    setIsLoadingAvailability(true);
    setHasAvailabilityData(false);

    api
      .getBookingAvailability({
        venueId: state.venueId,
        date: state.date.toISOString(),
        serviceId: state.serviceId,
        addOnIds: state.addOnIds
      })
      .then((result) => {
        if (!isCurrent) return;
        const slots = filterPastAvailabilitySlots(state.date, result?.slots || []);
        setAvailabilitySlots(slots);
        setHasAvailabilityData(true);
        setState((prev) => {
          if (!prev.time) return prev;
          const stillAvailable = slots.some((slot) => slot.time === prev.time && slot.available);
          return stillAvailable ? prev : { ...prev, time: null };
        });
      })
      .catch(() => {
        if (!isCurrent) return;
        setAvailabilitySlots([]);
        setHasAvailabilityData(false);
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoadingAvailability(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [state.date, state.venueId, state.serviceId, state.addOnIds]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (!event?.data || event.data.type !== 'opayo:3ds') return;
      setThreeDSData(null);
      if (!event.data.success) {
        const message = event.data.statusDetail || '3DS verification failed.';
        setFormError(message);
        setIsProcessingPayment(false);
        setPaymentResultModal({
          type: 'error',
          title: 'Payment Failed',
          message,
          shouldFinalize: false
        });
        return;
      }
      if (event.data.paymentId) {
        setPendingPaymentId(event.data.paymentId);
      }
      setPaymentResultModal({
        type: 'success',
        message: 'Payment verified successfully.',
        shouldFinalize: true
      });
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  useEffect(() => {
    if (!threeDSData) return;
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = threeDSData.acsUrl;
    form.target = 'opayo-3ds-iframe';
    const addField = (name: string, value: string) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };
    addField('creq', threeDSData.cReq);
    addField('TermUrl', threeDSData.termUrl);
    if (threeDSData.threeDSSessionData) {
      addField('threeDSSessionData', threeDSData.threeDSSessionData);
    }
    document.body.appendChild(form);
    form.submit();
    return () => {
      document.body.removeChild(form);
    };
  }, [threeDSData]);

  const getPrice = (item: Service | AddOn) => getTierPrice(item, { company: currentCompany });
  const selectedService = state.serviceId ? servicesById.get(state.serviceId) : undefined;
  const selectedServicePrice = selectedService != null ? Number(getPrice(selectedService)) : null;
  const requiresAddOnSelection = Boolean(
    selectedService &&
    selectedServicePrice != null &&
    selectedServicePrice <= 0 &&
    selectedService.name.toLowerCase().includes('health surveillance')
  );
  const selectedServiceId = state.serviceId;
  const compatibleAddOnIds = useMemo(
    () =>
      new Set(
        selectedServiceId
          ? addOns
              .filter((addon) => addon.compatibleServiceIds.includes(selectedServiceId))
              .map((addon) => addon.id)
          : []
      ),
    [addOns, selectedServiceId]
  );
  const hasCompatibleAddOn = state.addOnIds.some((id) => compatibleAddOnIds.has(id));
  const isAddOnSelectionMissing = requiresAddOnSelection && !hasCompatibleAddOn;
  const availableAddOns = useMemo(
    () =>
      addOns.filter(
        (addon) => !selectedServiceId || addon.compatibleServiceIds.includes(selectedServiceId)
      ),
    [addOns, selectedServiceId]
  );

  const getTotalDuration = useCallback(() => {
    let duration = selectedService?.durationMinutes || 0;
    state.addOnIds.forEach((id) => {
      const addon = addOnsById.get(id);
      if (addon) duration += addon.durationMinutes || 0;
    });
    return duration;
  }, [selectedService?.durationMinutes, state.addOnIds, addOnsById]);

  const goToStep = (step: number) => setState((prev) => ({ ...prev, step }));
  const handleScheduleContinue = () => {
    if (isAddOnSelectionMissing) return;
    if (!state.venueId || !state.serviceId || !state.date || !state.time) return;
    if (authenticatedCompany || currentCompany) {
      setState((prev) => ({ ...prev, mode: 'company', step: 2 }));
      return;
    }
    setState((prev) => ({ ...prev, mode: 'individual', step: 2 }));
  };

  const handleCalculateDistances = async () => {
    if (!userPostcode.trim()) return;
    setIsSearchingPostcode(true);
    setPostcodeError('');
    try {
      const response = await fetch(`https://api.postcodes.io/postcodes/${userPostcode.trim()}`);
      const data = await response.json();
      if (data.status === 200 && data.result) {
        const { latitude, longitude } = data.result;
        const newDistances: Record<string, number> = {};
        venues.forEach((venue) => {
          if (venue.coordinates) {
            const dist = getDistanceFromLatLonInMiles(
              latitude,
              longitude,
              venue.coordinates.lat,
              venue.coordinates.lng
            );
            newDistances[venue.id] = parseFloat(dist.toFixed(1));
          }
        });
        setDistances(newDistances);
      } else {
        setPostcodeError('Invalid UK Postcode');
      }
    } catch (e) {
      setPostcodeError('Unable to calculate distances');
    } finally {
      setIsSearchingPostcode(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (isAddOnSelectionMissing) {
      setFormError('Please select at least one add-on for Health Surveillance bookings.');
      return;
    }
    if (!isDobValid) {
      setDobTouched(true);
      setFormError(
        `Please enter a valid date of birth in DD-MM-YYYY format (year 1900-${new Date().getFullYear()}).`
      );
      return;
    }
    if (!isEmailValid) {
      setEmailTouched(true);
      setFormError('Please enter a valid email address.');
      return;
    }
    if (!isPhoneValid) {
      setPhoneTouched(true);
      setFormError('Please enter a valid UK phone number.');
      return;
    }
    const isCompanyBooking = state.mode === 'company' || !!authenticatedCompany;
    if (isCompanyBooking) {
      const normalizedBookingEmail = emailValue.toLowerCase();
      const reservedPortalEmails = new Set<string>();

      const companyEmail = String(currentCompany?.email || '')
        .trim()
        .toLowerCase();
      if (companyEmail) {
        reservedPortalEmails.add(companyEmail);
      }

      (currentCompany?.teamMembers || []).forEach((member) => {
        const memberEmail = String(member?.email || '')
          .trim()
          .toLowerCase();
        if (memberEmail) {
          reservedPortalEmails.add(memberEmail);
        }
      });

      if (normalizedBookingEmail && reservedPortalEmails.has(normalizedBookingEmail)) {
        setFormError(RESERVED_PORTAL_EMAIL_ERROR);
        return;
      }
    }
    const requiresCardPayment = !isCompanyBooking || currentCompany?.paymentPreference === 'card';
    const cleanCardNumber = cardDetails.number.replace(/\D/g, '');
    const cleanCvc = cardDetails.cvc.replace(/\D/g, '');
    if (isCompanyBooking && currentCompany?.requirePo && !state.customerDetails.poNumber?.trim()) {
      setFormError('Your company requires a Purchase Order (PO) number.');
      return;
    }
    if (requiresCardPayment && !cardDetails.cardholderName.trim()) {
      setFormError('Please enter the cardholder name.');
      return;
    }
    if (
      requiresCardPayment &&
      (cleanCardNumber.length < 12 ||
        cleanCardNumber.length > 19 ||
        cardDetails.expiry.length < 5 ||
        cleanCvc.length < 3 ||
        cleanCvc.length > 4)
    ) {
      setFormError('Please enter valid credit card details.');
      return;
    }
    if (
      requiresCardPayment &&
      (!billingAddress.address1.trim() ||
        !billingAddress.city.trim() ||
        !billingAddress.postalCode.trim() ||
        !billingAddress.country.trim())
    ) {
      setFormError('Please enter your billing address.');
      return;
    }
    if (requiresCardPayment && !/^[A-Za-z]{2}$/.test(billingAddress.country.trim())) {
      setFormError('Please select a valid billing country.');
      return;
    }
    if (!agreedToTerms) return;
    setIsProcessingPayment(true);
    if (requiresCardPayment) {
      if (!state.serviceId) {
        setFormError('Please select a service.');
        setIsProcessingPayment(false);
        return;
      }
      const bookingRef = pendingBookingRef || `K2Ref-${Math.floor(1000 + Math.random() * 9000)}`;
      setPendingBookingRef(bookingRef);
      try {
        const quote = await api.getCheckoutQuote({
          serviceId: state.serviceId,
          addOnIds: state.addOnIds,
          companyId: currentCompany?.id,
          currency: 'GBP'
        });
        setCheckoutPricing(quote.pricing);
        const totalAmount = quote.pricing.totalAmount;
        if (totalAmount > 0) {
          const expiryDigits = cardDetails.expiry.replace(/\D/g, '').slice(0, 4);
          const nameParts = state.customerDetails.name.trim().split(/\s+/).filter(Boolean);
          const firstName = nameParts[0] || 'Customer';
          const lastName = nameParts.slice(1).join(' ') || 'Customer';
          const session = await api.createOpayoSessionKey();
          const cardIdentifier = await tokenizeCardDetails({
            session,
            cardDetails: {
              number: cleanCardNumber,
              expiry: expiryDigits,
              cvc: cardDetails.cvc,
              cardholderName:
                cardDetails.cardholderName.trim() || state.customerDetails.name.trim() || 'Customer'
            }
          });

          const paymentResult = await api.processOpayoPayment({
            amount: totalAmount,
            expectedTotal: totalAmount,
            currency: 'GBP',
            description: `K2 Booking - ${selectedService?.name || 'Appointment'}`,
            merchantSessionKey: session.merchantSessionKey,
            cardIdentifier,
            billingAddress: {
              address1: billingAddress.address1.trim(),
              city: billingAddress.city.trim(),
              postalCode: billingAddress.postalCode.trim(),
              country: billingAddress.country.trim().toUpperCase()
            },
            customer: {
              firstName,
              lastName,
              email: state.customerDetails.email.trim(),
              phone: phoneValue ? normalizeUkPhone(phoneValue) : ''
            },
            browser: buildBrowserInfo(),
            serviceId: state.serviceId,
            addOnIds: state.addOnIds,
            companyId: currentCompany?.id,
            appointmentId: bookingRef
          });
          if (paymentResult?.paymentId) {
            setPendingPaymentId(paymentResult.paymentId);
          }
          if (
            paymentResult?.requires3DS &&
            paymentResult.acsUrl &&
            paymentResult.cReq &&
            paymentResult.termUrl
          ) {
            setThreeDSData({
              acsUrl: paymentResult.acsUrl,
              cReq: paymentResult.cReq,
              termUrl: paymentResult.termUrl,
              threeDSSessionData: paymentResult.threeDSSessionData
            });
            return;
          }
          setPaymentResultModal({
            type: 'success',
            message: 'Payment completed successfully.',
            shouldFinalize: true
          });
          return;
        }
      } catch (err: unknown) {
        const message = normalizeErrorMessage(err, 'Payment failed. Please try again.');
        setFormError(message);
        setPaymentResultModal({
          type: 'error',
          title: 'Payment Failed',
          message,
          shouldFinalize: false
        });
        setIsProcessingPayment(false);
        return;
      }
    }
    await finalizeBooking();
  };

  const closePaymentResultModal = () => {
    const shouldFinalize = Boolean(paymentResultModal?.shouldFinalize);
    setPaymentResultModal(null);
    if (shouldFinalize) {
      void finalizeBooking();
    }
  };

  const showInlineFormError = shouldShowInlineFormError(formError, paymentResultModal);

  const paymentResultModalElement = paymentResultModal ? (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 text-center border border-slate-200">
        <div
          className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${
            paymentResultModal.type === 'success'
              ? 'bg-green-50 text-green-600'
              : 'bg-red-50 text-red-600'
          }`}
        >
          {paymentResultModal.type === 'success' ? (
            <CheckCircle className="w-8 h-8" />
          ) : (
            <AlertCircle className="w-8 h-8" />
          )}
        </div>
        <h4 className="text-lg font-bold text-slate-900 mb-2">
          {paymentResultModal.title ||
            (paymentResultModal.type === 'success' ? 'Payment Successful' : 'Payment Failed')}
        </h4>
        <p className="text-slate-600 text-sm mb-6">{paymentResultModal.message}</p>
        <button
          onClick={closePaymentResultModal}
          className={`w-full py-2.5 rounded-xl text-sm font-bold text-white ${
            paymentResultModal.type === 'success'
              ? 'bg-green-600 hover:bg-green-700'
              : 'bg-red-600 hover:bg-red-700'
          }`}
        >
          {paymentResultModal.type === 'success' ? 'Continue' : 'Close'}
        </button>
      </div>
    </div>
  ) : null;

  if (state.step === 0) {
    let availableVenues =
      state.mode === 'company' && currentCompany?.allowedVenueIds?.length
        ? venues.filter((v) => currentCompany.allowedVenueIds!.includes(v.id))
        : venues;
    const sortedVenues = [...availableVenues].sort(
      (a, b) => (distances[a.id] ?? Infinity) - (distances[b.id] ?? Infinity)
    );
    const selectedVenue = state.venueId ? venuesById.get(state.venueId) : undefined;
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    const minBookableDate = new Date(today);
    const monthStart = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
    const monthEnd = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0);
    const dates = Array.from({ length: monthEnd.getDate() }, (_, i) => {
      return new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), i + 1);
    });
    const leadingEmptyDays = monthStart.getDay();
    const minMonthStart = new Date(minBookableDate.getFullYear(), minBookableDate.getMonth(), 1);
    const canGoPrevMonth = monthStart.getTime() > minMonthStart.getTime();
    const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const handleMonthChange = (offset: number) => {
      const nextMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + offset, 1);
      if (offset < 0 && nextMonth.getTime() < minMonthStart.getTime()) return;
      setVisibleMonth(nextMonth);
      setState((prev) => {
        if (!prev.date) return prev;
        const isDateInVisibleMonth =
          prev.date.getFullYear() === nextMonth.getFullYear() &&
          prev.date.getMonth() === nextMonth.getMonth();
        return isDateInVisibleMonth ? prev : { ...prev, date: null, time: null };
      });
    };
    const getMinutes = (t: string) => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    const requiredDuration = getTotalDuration();
    const generatedSlots = state.date
      ? getAvailableTimeSlotsForDate(state.date, selectedVenue?.schedule, {
          requiredDurationMinutes: requiredDuration
        })
      : [];
    const availabilityByTime = new Map(availabilitySlots.map((slot) => [slot.time, slot]));
    const availableSlots = state.date
      ? hasAvailabilityData
        ? availabilitySlots.map((slot) => slot.time)
        : generatedSlots
      : [];

    const fallbackBusyIntervals = !hasAvailabilityData
      ? appointments
          .filter(
            (a) =>
              a.venueId === state.venueId &&
              new Date(a.date).toDateString() === state.date?.toDateString() &&
              a.status !== 'cancelled'
          )
          .map((a) => {
            const appSvc = servicesById.get(a.serviceId);
            let dur = appSvc?.durationMinutes || 30;
            a.addOnIds.forEach((id) => {
              const ad = addOnsById.get(id);
              if (ad) dur += ad.durationMinutes;
            });
            const start = getMinutes(a.time);
            return { start, end: start + dur };
          })
      : [];
    const isSlotConflict = (time: string) => {
      const availabilitySlot = availabilityByTime.get(time);
      const slotStart = getMinutes(time);
      const slotEnd = slotStart + requiredDuration;
      const fallbackConflict = fallbackBusyIntervals.some(
        (interval) => slotStart < interval.end && slotEnd > interval.start
      );
      return availabilitySlot ? !availabilitySlot.available : fallbackConflict;
    };
    const hasSelectableSlot = availableSlots.some((time) => !isSlotConflict(time));
    const showNoSlots =
      Boolean(state.date) && !isLoadingAvailability && availableSlots.length === 0;
    const showFullyBooked =
      Boolean(state.date) &&
      !isLoadingAvailability &&
      availableSlots.length > 0 &&
      !hasSelectableSlot;
    const canContinue = Boolean(
      state.venueId && state.serviceId && state.date && state.time && !isAddOnSelectionMissing
    );

    return (
      <div className="max-w-4xl mx-auto px-4 py-8 animate-fade-in">
        {paymentResultModalElement}
        {onBack && (
          <button
            onClick={onBack}
            className="text-slate-500 hover:text-slate-800 mb-6 flex items-center gap-1 text-sm"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
        )}
        <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <span className="bg-teal-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-black">
            1
          </span>
          Select a Location
        </h2>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mb-6 flex flex-col sm:flex-row items-center gap-3">
          <div className="flex-1 w-full relative">
            <Navigation className="absolute left-3 top-2.5 w-5 h-5 text-slate-400" />
            <input
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-slate-50"
              placeholder="Enter UK Postcode..."
              value={userPostcode}
              onChange={(e) => setUserPostcode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCalculateDistances()}
            />
          </div>
          <button
            onClick={handleCalculateDistances}
            disabled={isSearchingPostcode || !userPostcode}
            className="bg-slate-900 text-white px-5 py-2 rounded-lg font-medium hover:bg-slate-800 disabled:opacity-50 w-full sm:w-auto"
          >
            {isSearchingPostcode ? 'Finding...' : 'Find Nearest'}
          </button>
        </div>
        {postcodeError && (
          <div className="mb-6 bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {postcodeError}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {sortedVenues.map((v) => {
            const mapUrl = getVenueMapUrl(v, { zoom: 13 });
            const mapsLink = getGoogleMapsUrl(v);
            const imageSrc =
              mapUrl || v.image || 'https://placehold.co/640x360/e2e8f0/64748b?text=No+Map';

            return (
              <div
                key={v.id}
                onClick={() =>
                  setState((prev) =>
                    prev.venueId === v.id
                      ? prev
                      : { ...prev, venueId: v.id, date: null, time: null }
                  )
                }
                className={`border rounded-xl overflow-hidden cursor-pointer transition-all hover:shadow-lg transform hover:-translate-y-1 relative ${state.venueId === v.id ? 'ring-2 ring-teal-500 border-teal-500' : 'border-slate-200'}`}
              >
                {distances[v.id] !== undefined && (
                  <div className="absolute top-2 right-2 bg-slate-900/90 text-white text-[10px] font-black px-2 py-1 rounded shadow-md z-10">
                    {distances[v.id]} MILES
                  </div>
                )}
                <div className="relative">
                  <img
                    src={imageSrc}
                    alt={v.name}
                    className="w-full h-32 object-cover"
                    loading="lazy"
                  />
                  {mapsLink && (
                    <a
                      href={mapsLink}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                      aria-label={`Open ${v.name} in Google Maps`}
                      className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 bg-white/90 text-slate-700 text-[10px] font-bold px-2 py-1 rounded-full shadow-sm"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Open in Maps
                    </a>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-slate-900">{v.name}</h3>
                  <p className="text-slate-500 text-xs mt-1">{v.address}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <span className="bg-teal-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-black">
              2
            </span>
            Choose Service
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {services.map((s) => {
              const price = getPrice(s);
              return (
                <div
                  key={s.id}
                  onClick={() =>
                    setState((p) => ({ ...p, serviceId: s.id, addOnIds: [], time: null }))
                  }
                  className={`border p-5 rounded-xl cursor-pointer transition-all ${state.serviceId === s.id ? 'bg-teal-50 border-teal-500 ring-2 ring-teal-200' : 'bg-white border-slate-200 hover:border-slate-300'}`}
                >
                  <div className="flex justify-between items-start">
                    <h3 className="font-bold text-slate-900">{s.name}</h3>
                    <span className="font-black text-teal-700">£{price} +VAT</span>
                  </div>
                  <p className="text-slate-500 text-xs mt-1 mb-4 h-8 line-clamp-2">
                    {s.description}
                  </p>
                  <div className="flex items-center text-[10px] font-bold text-slate-400 uppercase gap-3">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {s.durationMinutes} MINS
                    </span>
                    <span className="bg-slate-100 px-2 py-0.5 rounded-full">{s.category}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {state.serviceId && (
            <div className="mt-8 border-t pt-6">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                Enhance with Add-ons
                {requiresAddOnSelection && (
                  <span className="text-[10px] font-bold text-amber-600 uppercase">Required</span>
                )}
              </h3>
              <div className="space-y-3">
                {availableAddOns.map((a) => (
                  <label
                    key={a.id}
                    className={`flex items-center justify-between p-3 border rounded-xl cursor-pointer hover:bg-slate-50 transition-colors ${state.addOnIds.includes(a.id) ? 'bg-teal-50 border-teal-500' : 'border-slate-100'}`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        className="w-5 h-5 text-teal-600 rounded"
                        checked={state.addOnIds.includes(a.id)}
                        onChange={(e) =>
                          setState((p) => ({
                            ...p,
                            addOnIds: e.target.checked
                              ? [...p.addOnIds, a.id]
                              : p.addOnIds.filter((id) => id !== a.id)
                          }))
                        }
                      />
                      <div>
                        <div className="font-bold text-slate-800 text-sm">
                          {a.name}{' '}
                          <span className="text-[10px] font-normal text-slate-400 ml-1">
                            +{a.durationMinutes}m
                          </span>
                        </div>
                        <div className="text-[10px] text-slate-400">{a.description}</div>
                      </div>
                    </div>
                    <span className="font-bold text-teal-600">+£{getPrice(a)} +VAT</span>
                  </label>
                ))}
                {isAddOnSelectionMissing && (
                  <p className="text-xs font-semibold text-amber-600">
                    Select at least one add-on to continue with Health Surveillance.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-10">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <span className="bg-teal-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-black">
              3
            </span>
            Select Date & Time
          </h2>
          {!state.venueId ? (
            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 text-slate-500 text-sm text-center">
              Select a location to view availability.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="md:col-span-2">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <h3 className="font-bold text-slate-700 flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5 text-teal-600" /> Available Dates
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleMonthChange(-1)}
                      disabled={!canGoPrevMonth}
                      className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-teal-400 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                      aria-label="Previous month"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="text-sm font-bold text-slate-800 min-w-[140px] text-center">
                      {visibleMonth.toLocaleDateString('en-GB', {
                        month: 'long',
                        year: 'numeric'
                      })}
                    </div>
                    <button
                      onClick={() => handleMonthChange(1)}
                      className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-teal-400 flex items-center justify-center"
                      aria-label="Next month"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-7 gap-2 mb-2">
                  {weekdayLabels.map((label) => (
                    <div
                      key={label}
                      className="text-[10px] font-bold text-slate-400 text-center uppercase"
                    >
                      {label}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {Array.from({ length: leadingEmptyDays }).map((_, idx) => (
                    <div key={`empty-${idx}`} />
                  ))}
                  {dates.map((d) => {
                    const isSelected = state.date?.toDateString() === d.toDateString();
                    const { blocked, reason } = isDateBlockedBySchedule(
                      d,
                      selectedVenue?.schedule,
                      'Closed on this day'
                    );
                    const beforeBookingWindow = d.getTime() < minBookableDate.getTime();
                    const isToday = d.toDateString() === now.toDateString();
                    const hasSlotsToday =
                      isToday && selectedVenue
                        ? getAvailableTimeSlotsForDate(d, selectedVenue?.schedule, {
                            requiredDurationMinutes: requiredDuration
                          }).length > 0
                        : true;
                    const noSlotsLeftToday = !blocked && isToday && !hasSlotsToday;
                    const isUnavailable = blocked || beforeBookingWindow || noSlotsLeftToday;
                    return (
                      <button
                        key={d.toISOString()}
                        disabled={isUnavailable}
                        title={
                          beforeBookingWindow
                            ? 'Bookings cannot be in the past.'
                            : blocked
                              ? reason
                              : noSlotsLeftToday
                                ? 'No remaining slots today.'
                                : reason
                        }
                        onClick={() => {
                          setNextAvailableMessage(null);
                          setState((prev) => ({ ...prev, date: d, time: null }));
                        }}
                        className={`py-3 px-1 rounded-xl border text-center transition-all relative ${isSelected ? 'bg-teal-600 text-white border-teal-600 shadow-lg' : 'bg-white hover:border-teal-400 shadow-sm'} ${isUnavailable ? 'opacity-40 cursor-not-allowed bg-slate-50' : ''}`}
                      >
                        <div className="text-[10px] font-bold uppercase opacity-75">
                          {d.getDate()}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 h-fit shadow-inner">
                <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-teal-600" /> Select Time
                </h3>
                {!state.serviceId ? (
                  <p className="text-slate-500 text-sm text-center py-10">
                    Select a service to see times.
                  </p>
                ) : !state.date ? (
                  <p className="text-slate-500 text-sm text-center py-10">
                    Select a date to see times.
                  </p>
                ) : isLoadingAvailability ? (
                  <p className="text-slate-500 text-sm text-center py-10">
                    Loading availability...
                  </p>
                ) : showNoSlots ? (
                  <p className="text-slate-400 text-sm italic text-center py-10">
                    No slots available for this day.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {availableSlots.map((time) => {
                      const availabilitySlot = availabilityByTime.get(time);
                      const isConflict = isSlotConflict(time);
                      return (
                        <button
                          key={time}
                          disabled={isConflict}
                          onClick={() => {
                            setNextAvailableMessage(null);
                            setState((prev) => ({ ...prev, time }));
                          }}
                          className={`py-2 px-3 text-xs rounded-lg border transition-all font-black ${state.time === time ? 'bg-teal-600 text-white border-teal-600' : isConflict ? 'bg-red-50 text-red-300 border-red-100 cursor-not-allowed line-through' : 'bg-white text-slate-700 border-slate-200 hover:border-teal-400 shadow-sm'}`}
                        >
                          {time}
                          {availabilitySlot && availabilitySlot.capacity > 1
                            ? ` (${availabilitySlot.remaining} left)`
                            : ''}
                        </button>
                      );
                    })}
                  </div>
                )}
                {(showNoSlots || showFullyBooked) && (
                  <div className="mt-4 bg-white border border-slate-200 rounded-xl p-3 text-center">
                    <p className="text-xs text-slate-500">
                      {showFullyBooked
                        ? 'This day is fully booked.'
                        : 'No appointments available on this day.'}
                    </p>
                    <button
                      onClick={findNextAvailableSlot}
                      disabled={isFindingNextAvailable}
                      className="mt-2 inline-flex items-center justify-center px-3 py-1.5 text-xs font-bold rounded-lg border border-teal-200 text-teal-700 bg-teal-50 hover:bg-teal-100 disabled:opacity-50"
                    >
                      {isFindingNextAvailable ? 'Searching...' : 'Find next available'}
                    </button>
                  </div>
                )}
                {nextAvailableMessage && (
                  <p className="mt-3 text-[11px] text-slate-500 text-center">
                    {nextAvailableMessage}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-10 flex justify-end">
          <button
            onClick={handleScheduleContinue}
            disabled={!canContinue}
            className="bg-slate-900 text-white px-10 py-3 rounded-xl font-bold hover:bg-slate-800 shadow-lg shadow-slate-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
          >
            Continue <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  if (state.step === 2) {
    const pricing = getPricingForCheckout();
    const total = pricing.totalAmount;
    const duration = getTotalDuration();
    const venue = state.venueId ? venuesById.get(state.venueId) : undefined;
    const requiresCardPayment =
      !state.mode?.includes('company') || currentCompany?.paymentPreference === 'card';
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 animate-fade-in">
        {paymentResultModalElement}
        <button
          onClick={() => goToStep(0)}
          className="text-slate-500 hover:text-slate-800 mb-4 flex items-center gap-1 text-sm"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <span className="bg-teal-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-black">
            4
          </span>
          Confirm Booking
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 h-fit">
            <h3 className="font-bold text-lg mb-4 text-slate-800 border-b pb-2">Order Summary</h3>
            <div className="space-y-4 text-sm">
              <div className="flex justify-between">
                <div>
                  <span className="text-slate-500 block text-[10px] font-bold uppercase tracking-widest">
                    Service
                  </span>
                  <span className="font-bold text-slate-900">{selectedService?.name}</span>
                </div>
                <span className="font-black text-slate-700">
                  £{selectedService ? getPrice(selectedService) : 0}
                </span>
              </div>
              {state.addOnIds.length > 0 && (
                <div>
                  <span className="text-slate-500 block text-[10px] font-bold uppercase tracking-widest mb-1">
                    Add-ons
                  </span>
                  {state.addOnIds.map((id) => {
                    const ad = addOnsById.get(id);
                    return (
                      <div key={id} className="flex justify-between mt-1 text-xs">
                        <span className="text-slate-600">{ad?.name}</span>
                        <span className="font-bold text-slate-700">£{ad ? getPrice(ad) : 0}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="pt-4 border-t border-slate-200">
                <div>
                  <span className="text-slate-500 block text-[10px] font-bold uppercase tracking-widest">
                    Location
                  </span>
                  <span className="font-bold text-slate-900">{venue?.name}</span>
                  <p className="text-xs text-slate-400">{venue?.address}</p>
                </div>
              </div>
              <div className="pt-4 border-t border-slate-200 space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold uppercase tracking-widest">
                    Subtotal
                  </span>
                  <span className="font-bold text-slate-700">
                    £{pricing.subtotalAmount.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 font-bold uppercase tracking-widest">
                    VAT ({pricing.vatRatePercent.toFixed(2)}%)
                  </span>
                  <span className="font-bold text-slate-700">£{pricing.vatAmount.toFixed(2)}</span>
                </div>
              </div>
              <div className="flex justify-between pt-4 border-t border-slate-200 text-xl font-black text-slate-900 uppercase tracking-tight">
                <span>Total Value</span>
                <span className="text-teal-600">£{total.toFixed(2)}</span>
              </div>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-white p-6 border-2 border-slate-100 rounded-2xl shadow-sm">
              <h4 className="font-bold mb-4 text-slate-800 flex items-center gap-2">
                <UserIcon className="w-5 h-5 text-teal-600" /> Patient Information
              </h4>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                      Full Name
                    </label>
                    <input
                      required
                      className="w-full border rounded-lg px-3 py-2 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 text-sm font-medium"
                      value={state.customerDetails.name}
                      onChange={(e) =>
                        setState((p) => ({
                          ...p,
                          customerDetails: { ...p.customerDetails, name: e.target.value }
                        }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                      Date of Birth
                    </label>
                    <input
                      required
                      type="text"
                      inputMode="numeric"
                      placeholder="DD-MM-YYYY"
                      maxLength={10}
                      className="w-full border rounded-lg px-3 py-2 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 text-sm font-medium"
                      value={state.customerDetails.dob}
                      onChange={(e) =>
                        setState((p) => ({
                          ...p,
                          customerDetails: {
                            ...p.customerDetails,
                            dob: formatDobInput(e.target.value)
                          }
                        }))
                      }
                      onBlur={() => setDobTouched(true)}
                    />
                    {showDobError && (
                      <p className="mt-1 text-xs font-semibold text-red-600">
                        Use DD-MM-YYYY. Year must be between 1900 and {new Date().getFullYear()}.
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                    Email Address
                  </label>
                  <input
                    required
                    type="email"
                    className="w-full border rounded-lg px-3 py-2 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 text-sm font-medium"
                    value={state.customerDetails.email}
                    onChange={(e) =>
                      setState((p) => ({
                        ...p,
                        customerDetails: { ...p.customerDetails, email: e.target.value }
                      }))
                    }
                    onBlur={() => setEmailTouched(true)}
                  />
                  {showEmailError && (
                    <p className="mt-1 text-xs font-semibold text-red-600">
                      Enter a valid email address.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                    Mobile Number (optional)
                  </label>
                  <input
                    type="tel"
                    className="w-full border rounded-lg px-3 py-2 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 text-sm font-medium"
                    value={state.customerDetails.phone}
                    onChange={(e) =>
                      setState((p) => ({
                        ...p,
                        customerDetails: { ...p.customerDetails, phone: e.target.value }
                      }))
                    }
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      setPhoneTouched(true);
                      if (value && isValidUkPhone(value)) {
                        setState((p) => ({
                          ...p,
                          customerDetails: { ...p.customerDetails, phone: normalizeUkPhone(value) }
                        }));
                      }
                    }}
                  />
                  <p className="mt-1 text-[10px] text-slate-400">
                    UK numbers only, e.g. 07123 456789 or +44 7123 456789.
                  </p>
                  {showPhoneError && (
                    <p className="mt-1 text-xs font-semibold text-red-600">
                      Enter a valid UK phone number.
                    </p>
                  )}
                </div>
                {(state.mode === 'company' || authenticatedCompany) && (
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                      PO Number {currentCompany?.requirePo && '*'}
                    </label>
                    <input
                      required={currentCompany?.requirePo}
                      className="w-full border rounded-lg px-3 py-2 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500 text-sm font-medium"
                      value={state.customerDetails.poNumber}
                      onChange={(e) =>
                        setState((p) => ({
                          ...p,
                          customerDetails: { ...p.customerDetails, poNumber: e.target.value }
                        }))
                      }
                    />
                  </div>
                )}
              </div>
            </div>
            {requiresCardPayment && (
              <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden">
                <div className="relative z-10 mb-4 space-y-2">
                  <h4 className="text-xs font-black uppercase tracking-[0.2em] text-teal-400">
                    Secure Payment
                  </h4>
                  <div className="flex items-center gap-1 text-[9px] font-bold text-slate-400 bg-white/5 px-2 py-1 rounded w-fit">
                    <ShieldCheck className="w-3 h-3 text-teal-500" />
                    SECURED BY OPAYO
                  </div>
                </div>
                <div className="relative z-10 space-y-3">
                  <input
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none focus:border-teal-500"
                    placeholder="Card Number"
                    maxLength={19}
                    value={cardDetails.number}
                    onChange={(e) =>
                      setCardDetails({ ...cardDetails, number: formatCardNumber(e.target.value) })
                    }
                  />
                  <input
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none focus:border-teal-500"
                    placeholder="Cardholder Name"
                    maxLength={50}
                    value={cardDetails.cardholderName}
                    onChange={(e) =>
                      setCardDetails({ ...cardDetails, cardholderName: e.target.value })
                    }
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none focus:border-teal-500"
                      placeholder="MM / YY"
                      maxLength={5}
                      value={cardDetails.expiry}
                      onChange={(e) =>
                        setCardDetails({ ...cardDetails, expiry: formatExpiry(e.target.value) })
                      }
                    />
                    <input
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none focus:border-teal-500"
                      placeholder="CVC"
                      maxLength={4}
                      value={cardDetails.cvc}
                      onChange={(e) =>
                        setCardDetails({
                          ...cardDetails,
                          cvc: e.target.value.replace(/[^0-9]/g, '')
                        })
                      }
                    />
                  </div>
                  <input
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none focus:border-teal-500"
                    placeholder="Billing Address"
                    value={billingAddress.address1}
                    onChange={(e) =>
                      setBillingAddress({ ...billingAddress, address1: e.target.value })
                    }
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none focus:border-teal-500"
                      placeholder="City"
                      value={billingAddress.city}
                      onChange={(e) =>
                        setBillingAddress({ ...billingAddress, city: e.target.value })
                      }
                    />
                    <input
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none focus:border-teal-500"
                      placeholder="Postcode"
                      value={billingAddress.postalCode}
                      onChange={(e) =>
                        setBillingAddress({ ...billingAddress, postalCode: e.target.value })
                      }
                    />
                  </div>
                  <select
                    className="w-full bg-slate-900 border border-white/10 rounded-xl px-4 py-2 text-white text-sm outline-none focus:border-teal-500"
                    style={{ colorScheme: 'dark' }}
                    value={billingAddress.country}
                    onChange={(e) =>
                      setBillingAddress({ ...billingAddress, country: e.target.value })
                    }
                  >
                    {COUNTRY_OPTIONS.map((option) => (
                      <option
                        key={option.code}
                        value={option.code}
                        style={{ backgroundColor: '#0f172a', color: '#f8fafc' }}
                      >
                        {option.label} ({option.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="relative z-10 mt-4 border-t border-white/10 pt-3 space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400 font-medium">Subtotal</span>
                    <span className="text-white font-bold">
                      £{pricing.subtotalAmount.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400 font-medium">
                      VAT ({pricing.vatRatePercent.toFixed(2)}%)
                    </span>
                    <span className="text-white font-bold">£{pricing.vatAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-white/10 pt-2">
                    <span className="text-slate-300 font-semibold">Transaction Total</span>
                    <span className="text-white text-base font-black">
                      £{pricing.totalAmount.toFixed(2)}
                    </span>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-center gap-3 grayscale opacity-40">
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/2560px-Visa_Inc._logo.svg.png"
                    className="h-2 w-auto"
                    alt="Visa"
                  />
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/1280px-Mastercard-logo.svg.png"
                    className="h-4 w-auto"
                    alt="Mastercard"
                  />
                </div>
              </div>
            )}
            <div className="bg-slate-100 p-4 rounded-xl border border-slate-200">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-5 h-5 text-teal-600 rounded mt-0.5"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  required
                />
                <div className="text-[10px] text-slate-500 leading-relaxed font-medium">
                  I understand and agree to the booking terms including the 24h cancellation policy.
                </div>
              </label>
            </div>
            {showInlineFormError && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-xs font-bold flex items-center gap-2 border border-red-100">
                <AlertCircle className="w-4 h-4" /> {formError}
              </div>
            )}
            <button
              type="submit"
              disabled={isProcessingPayment || !agreedToTerms}
              className={`w-full text-white py-4 rounded-2xl font-black text-lg transition-all shadow-xl disabled:opacity-50 active:scale-95 uppercase tracking-widest ${isProcessingPayment ? 'bg-slate-700' : 'bg-teal-600 hover:bg-teal-700 shadow-teal-600/20'}`}
            >
              {isProcessingPayment ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" /> Authorizing...
                </div>
              ) : requiresCardPayment ? (
                `Pay £${total.toFixed(2)} & Book`
              ) : (
                'Confirm Booking'
              )}
            </button>
          </form>
        </div>
        {threeDSData && (
          <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-bold text-slate-800">3D Secure Verification</h4>
                <span className="text-xs text-slate-400">Complete the challenge to continue</span>
              </div>
              <iframe
                title="3DS Verification"
                name="opayo-3ds-iframe"
                className="w-full h-[70vh] border rounded-xl"
              />
            </div>
          </div>
        )}
      </div>
    );
  }
  return null;
};
